import * as angular from "angular";
import { escape } from "lodash";
import confirmClear from "../../../behaviors/confirm-clear";
import choicesSelect from "../../../components/choices-select/choices-select";
import selectBox from "../../../behaviors/select-box";
import template from "./choice-control.html";

export interface SelectableChoice {
	value: unknown;
	label: string;
}

interface WidgetChoice {
	value: string;
	label: string;
	modelValue: unknown;
}

interface ChoicesBox {
	clearSelected(): void;
	setChoices(choices: WidgetChoice[], valueProperty: string, labelProperty: string, replace: boolean): void;
	setValueByChoice(value: string): void;
}

export interface ChoiceControlController {
	choices: SelectableChoice[];
	value: unknown;
	enabled: boolean;
	selectionEnabled?: boolean;
	title: string;
	inputId: string;
	required?: boolean;
	invalid?: boolean;
	describedBy?: string;
	onChange(args: { value: unknown }): void;
}

// AngularJS assigns bindings before constructing the controller.
// eslint-disable-next-line no-redeclare
export class ChoiceControlController implements angular.IController {
	public static $inject = ["$scope", "$element"];

	public selected?: string;

	private widgetChoices: WidgetChoice[] = [];

	private box?: ChoicesBox;

	private synchronizing = false;

	private readonly originalDescriptions = new WeakMap<Element, string>();

	public constructor(
		private readonly $scope: angular.IScope,
		private readonly $element: angular.IAugmentedJQuery
	) {}

	public $onChanges(changes: angular.IOnChangesObject): void {
		if(changes.choices) {
			this.widgetChoices = (this.choices ?? []).map((choice, index) => ({
				value: String(index),
				label: escape(choice.label),
				modelValue: choice.value
			}));
			this.populate();
		} else if(changes.value) {
			this.render();
		}

		this.$scope.$evalAsync(() => this.updateAccessibility());
	}

	public setBox(box: ChoicesBox): void {
		this.box = box;
		this.populate();
		this.$scope.$evalAsync(() => this.updateAccessibility());
	}

	public display(value: string): string {
		return this.widgetChoices.find(choice => choice.value === value)?.label ?? "";
	}

	public change(value?: string): void {
		if(this.enabled && this.selectionEnabled !== false && !this.synchronizing) {
			this.onChange({ value: this.widgetChoices.find(choice => choice.value === value)?.modelValue });
		}
	}

	public clear(): void {
		if(this.enabled) {
			this.onChange({ value: undefined });
		}
	}

	private populate(): void {
		if(this.box) {
			this.box.clearSelected();
			this.box.setChoices(this.widgetChoices, "value", "label", true);
		}

		this.render();
	}

	private render(): void {
		this.synchronizing = true;
		const selected = this.widgetChoices.find(choice => angular.equals(choice.modelValue, this.value));
		this.selected = selected?.value;
		this.box?.clearSelected();

		if(selected) {
			this.box?.setValueByChoice(selected.value);
		}

		this.synchronizing = false;
	}

	private updateAccessibility(): void {
		for(const element of this.$element[0].querySelectorAll("select, input, [role='combobox']")) {
			if(!this.originalDescriptions.has(element)) {
				this.originalDescriptions.set(element, element.getAttribute("aria-describedby") ?? "");
			}

			element.setAttribute("aria-required", String(Boolean(this.required)));
			element.setAttribute("aria-invalid", String(Boolean(this.invalid)));
			const describedBy = [this.originalDescriptions.get(element), this.describedBy].filter(Boolean).join(" ");

			if(describedBy) {
				element.setAttribute("aria-describedby", describedBy);
			} else {
				element.removeAttribute("aria-describedby");
			}
		}
	}
}

export default angular.module("vesl.extensions.json-schema.choice-control", [choicesSelect, selectBox, confirmClear])
	.component("jsonSchemaChoiceControl", {
		template,
		controller: ChoiceControlController,
		bindings: {
			choices: "<",
			value: "<",
			enabled: "<",
			selectionEnabled: "<?",
			title: "<fieldTitle",
			inputId: "<",
			required: "<?",
			invalid: "<?",
			describedBy: "<?",
			onChange: "&"
		}
	})
	.name;
